import { ArrowPathIcon } from "@heroicons/react/24/outline"
import { useQueryClient } from "@tanstack/react-query"
import { useInvites, useUser } from "api/ApiHooks"
import { LoversApi } from "api/LoversApi"
import { useState } from "react"
import { classNames } from "utils/ClassNames"

export const InvitationsPage = () => {
  const { data, refetch, isFetching } = useInvites()

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-x-2">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Invitations</h1>
        <button
          onClick={() => refetch()}
          className={classNames("transition-transform hover:text-primary-500")}
        >
          <ArrowPathIcon className="w-6 h-6 transition-colors" />
        </button>
      </div>
      {!data || data.invites.length === 0 ? (
        <EmptyState isChecking={isFetching} />
      ) : (
        <InvitationsList invites={data.invites} />
      )}
    </div>
  )
}

const EmptyState = (props: { isChecking: boolean }) => {
  const { isChecking } = props
  const { data } = useUser()

  return (
    <div className="flex flex-col h-full">
      <p className="mt-4 text-gray-600 dark:text-gray-300">
        {isChecking ? "Loading..." : "All invitations have been responded to! 🎉"}
      </p>

      {!isChecking && !!data?.user.email && (
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          People can invite you by your email{" "}
          <span className="font-semibold">{data.user.email}</span>
        </p>
      )}
    </div>
  )
}

const InvitationsList = (props: {
  invites: {
    id: string
    timeCapsuleId: string
    timeCapsuleName: string
    fromName?: string
  }[]
}) => {
  const { invites } = props
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const respondToInvite = (inviteId: string, accept: boolean) => {
    LoversApi()
      .self.respondToTimeCapsuleInvite(inviteId, { isAccepting: accept })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["useInvites"] })
        if (accept) {
          queryClient.invalidateQueries({ queryKey: ["useSharedTimeCapsules"] })
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <ul role="list" className="divide-y divide-gray-100 dark:divide-gray-800">
      {invites.map((invite) => (
        <li key={invite.id} className="flex items-center justify-between gap-x-6 py-5">
          <div className="min-w-0">
            <div className="flex items-start gap-x-3">
              <p className="text-sm/6 font-semibold text-gray-900 dark:text-white">
                {invite.timeCapsuleName}
              </p>
            </div>
            <div className="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500 dark:text-gray-400">
              <p className="whitespace-nowrap">
                Invited by {invite.fromName || "Anonymous"}
              </p>
            </div>
          </div>
          <div className="flex flex-none items-center gap-x-4">
            <button
              onClick={() => respondToInvite(invite.id, false)}
              disabled={isLoading}
              className="rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
            >
              Reject<span className="sr-only">, {invite.timeCapsuleName}</span>
            </button>
            <button
              onClick={() => respondToInvite(invite.id, true)}
              disabled={isLoading}
              className="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 transition-colors"
            >
              Accept invite<span className="sr-only">, {invite.timeCapsuleName}</span>
            </button>
          </div>
        </li>
      ))}
    </ul>
  )
}
